<template>
  <div>
    <b-card no-body class="mx-n3 my-0">
      <b-row class="mx-1 mt-2" align-h="between">
        <b-col lg="7" md="12" sm="12">
          <b-row align-h="between">
            <b-col lg="4" md="4" sm="12" class="mb-1">
              <div class="form-select">
                Show
                <b-form-select
                  v-model="perPage"
                  :options="options"
                  style="width: 60px;"
                  size="sm"
                ></b-form-select>
                Entries
              </div>
            </b-col>
          </b-row>
        </b-col>
        <b-col lg="5" md="12" sm="12" class="mb-1">
          <b-row align-h="end">
            <SearchBar :hasErrorMessage="true">
              <template #input>
                <b-form-input v-model="filter" type="search" placeholder="Search" />
              </template>
            </SearchBar>
          </b-row>
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <div v-if="hasData">
            <b-table
              id="xtend-probe-metrics-table"
              class="table-with-actions-min-height px-2"
              responsive
              outlined
              :items="probeMetricsFilter"
              :fields="computedTableFields"
              :per-page="perPage"
              :current-page="currentPage"
              :filter="filter"
              @filtered="onFiltered"
              select-mode="single"
              selectable
              @row-selected="onRowSelected"
            >
              <template
                #cell(rtd)="row"
              >
                {{ `${row.value} ms` }}
              </template>
              <template
                #cell(jitter)="row"
              >
                {{ `${row.value} ms` }}
              </template>
              <template
                #cell(loss)="row"
              >
                {{ `${row.value}%` }}
              </template>
              <template #head(rtd)>
                <span v-b-tooltip.hover title="Median RTD Value">RTD</span>
              </template>
              <template #head(jitter)>
                <span v-b-tooltip.hover title="Median Jitter Value">Jitter</span>
              </template>
              <template #head(loss)>
                <span v-b-tooltip.hover title="Median Loss Value">Loss</span>
              </template>
            </b-table>
            <b-pagination
              v-model="currentPage"
              :total-rows="paginationTotalRows"
              :per-page="perPage"
              align="right"
              first-number
              last-number
              prev-class="prev-item"
              next-class="next-item"
              class="mb-2 mx-1 mx-1"
            />
          </div>
          <div v-else-if="loaded" class="mx-1 pl-1 mb-1 text-center">
            {{ $t("No Probes Data Available") }}
            <b-icon icon="hand-thumbs-up"/>
          </div>
        </b-col>
      </b-row>
    </b-card>
    <b-card
      no-body class="mx-n3 mt-1"
      v-if="hasSelectedRow"
    >
      <b-row class="mx-1 my-1" align-h="between">
        <b-col lg="7" md="12" sm="12">
          <b-row align-h="between">
            <b-col>
              <h4>{{ metricsDetailsTitle }}</h4>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
      <b-row>
        <b-col lg="12" md="12" sm="12">
          <b-row class="mx-1 mt-2"  align-h="between">
            <b-col
              lg="6" md="6" sm="12" class="mb-1"
              v-for="(metric, index) in metrics"
              :key="index"
            >
              <h6>{{ metricsToLabel[metric] }}</h6>
              <ProbeMetrics
                :timeRange="timeRange"
                :xtendUrl="getXtendProbeMetricTsUrl"
                :metric="metric"
                :targetDestination="getTargetDestination"
              ></ProbeMetrics>
            </b-col>
          </b-row>
        </b-col>
      </b-row>
    </b-card>
  </div>
</template>

<script>
import SearchBar from '@/xvisor/components/SearchBar.vue';
import collapseText from '@/xvisor/utilities/collapseText';
import hostnameFormatter from '@/xvisor/utilities/hostnameFormatter';
import formatNumberWithCommas from '@/xvisor/utilities/addCommaToNumber';
import ProbeMetrics from '@/xvisor/components/app/networkTab/ProbeMetrics.vue';

export default {
  props: {
    timeRange: {
      type: Object,
      required: true,
    },
    shortTimeRange: {
      type: Object,
      required: true,
    },
    summaryUrl: {
      type: String,
      required: true,
    },
    tsUrl: {
      type: String,
      required: true,
    },
  },
  components: {
    SearchBar,
    ProbeMetrics,
  },
  data() {
    return {
      data: [],
      loaded: false,
      currentPage: 1,
      perPage: 10,
      options: [
        { value: 5, text: '5' },
        { value: 10, text: '10' },
        { value: 15, text: '15' },
      ],
      filter: null,
      filteredLength: null,
      tableFields: [
        { key: 'destination', label: 'Destination', sortable: true },
        { key: 'rtd', label: 'RTD', sortable: true },
        { key: 'jitter', label: 'Jitter', sortable: true },
        { key: 'loss', label: 'Loss', sortable: true },
      ],
      selected: [],
      metrics: ['xtend_jitter', 'xtend_rtd', 'xtend_loss'],
      metricsToLabel: {
        xtend_jitter: 'Jitter',
        xtend_rtd: 'RTD',
        xtend_loss: 'Loss',
      },
    };
  },
  computed: {
    hasData() {
      return this.loaded && this.data && this.data.length > 0;
    },
    hasSelectedRow() {
      return this.selected && this.selected.length > 0;
    },
    paginationTotalRows() {
      if (this.filter) return this.filteredLength;
      return this.data.length;
    },
    computedTableFields() {
      return this.tableFields;
    },
    probeMetricsFilter() {
      const retVal = this.data;
      return retVal;
    },
    getXtendProbeMetricTsUrl() {
      return this.tsUrl;
    },
    metricsDetailsTitle() {
      return `Metric Details: ${this.getTargetDestination}`;
    },
    getTargetDestination() {
      return this.selected[0].destination;
    },
  },
  watch: {
    summaryUrl() {
      this.httpGet();
    },
    timeRange() {
      this.httpGet();
    },
    shortTimeRange() {
      this.httpGet();
    },
  },
  mounted() {
    this.httpGet();
  },
  methods: {
    httpGet() {
      this.loaded = false;
      this.selected = [];
      this.$http
        .get(this.summaryUrl, {
          params: {
            start: this.timeRange.start.toISOString(),
            end: this.timeRange.end.toISOString(),
          },
        })
        .then((response) => { this.data = response.data; })
        .finally(() => { this.loaded = true; });
    },
    onFiltered(filteredItems) {
      this.filteredLength = filteredItems.length;
      this.currentPage = 1;
    },
    onRowSelected(items) {
      this.selected = items;
    },
    collapseText,
    hostnameFormatter,
    formatNumberWithCommas,
  },
};
</script>
